import { TransactionState } from '@app/store/domain';

export const CONTENT_TYPES = ['SOCIAL_POST', 'BLOG_POST', 'EMAIL'] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

/**
 * Temporary ID used for optimistic content entries before API response
 */
export const OPTIMISTIC_CONTENT_ID = 'pending';

export type ContentRepurposingStatus =
  | 'INITIAL'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'FAILED';

export type ContentRepurposingEligibility =
  | 'PENDING_CHECKS'
  | 'ELIGIBLE'
  | 'INELIGIBLE';

export type ContentGenerationStatus =
  | 'initial'
  | 'in_progress'
  | 'finished'
  | 'failed';

export type ContentRepurposingTopic = {
  id: string;
  topic: string;
};

export type ContentRepurposingTopics = {
  status: ContentRepurposingStatus;
  value: ContentRepurposingTopic[] | null;
};

export type ContentRepurposingToneOfVoice = {
  status: ContentRepurposingStatus;
  value: string | null;
};

export type ToneOfVoiceOption = {
  key: string;
  toneOfVoice: string;
  description: string;
};

export const DEFAULT_TONE_OF_VOICE_ID = 'professional_and_informative';

export type ToneOfVoiceOptionsResponse = {
  toneOfVoiceOptions: ToneOfVoiceOption[];
};

export type EmailTypeOption = {
  key: string;
  labelKey: string;
};

export type EmailAudienceMember = {
  key: string;
  labelKey: string;
  types: EmailTypeOption[];
};

export type EmailRecipientsWithTypesResponse = {
  emailAudience: EmailAudienceMember[];
};

export type ContentRepurposingResponse =
  | {
      eligibility: Extract<
        ContentRepurposingEligibility,
        'INELIGIBLE' | 'PENDING_CHECKS'
      >;
    }
  | {
      eligibility: Extract<ContentRepurposingEligibility, 'ELIGIBLE'>;
      topics: ContentRepurposingTopics;
      toneOfVoice: ContentRepurposingToneOfVoice | null;
    };

export type ContentHistoryEntry = {
  id: string;
  status: ContentGenerationStatus;
  createdAt: string;
};

export type ContentHistory = {
  history: ContentHistoryEntry[];
  cursor: string | null;
} & TransactionState;

export type BaseContentGenerationAttributes = {
  toneOfVoiceId: string | null;
  topicId: string | null;
};

export type EmailContentGenerationAttributes =
  BaseContentGenerationAttributes & {
    emailAudienceId: string | null;
    emailTypeId: string | null;
  };

export type ContentGenerationAttributesByType = {
  SOCIAL_POST: BaseContentGenerationAttributes;
  BLOG_POST: BaseContentGenerationAttributes;
  EMAIL: EmailContentGenerationAttributes;
};

export type ContentGenerationAttributes =
  ContentGenerationAttributesByType[ContentType];

export type GeneratedContent = {
  status: ContentGenerationStatus;
  topics: string[];
  toneVoiceId: string;
  contentType: string;
  context: Record<string, string>;
  content: { options: string[] } | null;
  error?: string;
};

export type ApiContentRepurposingStatus =
  | 'INITIAL'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'FAILED';

export type ApiContentHistoryItem = {
  id: string;
  status: ApiContentRepurposingStatus;
  createdAt: string;
  contentType: ContentType;
};

export type ApiContentHistoryResponse = {
  cursor: string | null;
  data: ApiContentHistoryItem[];
};

export type ApiContentGenerationRequest = {
  topics: string[]; // Required, min 1 item
  toneOfVoice: string; // Required, tone of voice external ID
  contentType: ContentType; // Required
  context?: Record<string, string>; // Optional, e.g., { recipientTypeId: '...', emailTypeId: '...' }
};

export type ApiContentResponse =
  | {
      id: string;
      status: 'FINISHED';
      contentType: ContentType;
      topics: string[];
      toneOfVoice: string | null;
      context?: Record<string, unknown>;
      content: string;
    }
  | {
      id: string;
      status: 'INITIAL' | 'IN_PROGRESS';
      contentType: ContentType;
      topics: string[];
      toneOfVoice: string | null;
      context?: Record<string, unknown>;
      content: null;
    }
  | {
      id: string;
      status: 'FAILED';
      contentType: ContentType;
      topics: string[];
      toneOfVoice: string | null;
      context?: Record<string, unknown>;
      content: null;
      error: string;
    };

export type PusherContentType = 'TONE_OF_VOICE' | 'TOPIC_SUMMARY';

export type ContentRepurposePusherUpdate = {
  contentType: PusherContentType;
  status: ContentGenerationStatus;
};

export type ContentGenerationPusherUpdate = {
  recordingId: string;
  contentId: string;
  contentType: ContentType;
  jobId: string;
  status: ContentGenerationStatus;
};
